import { createContext } from "react";
import { FeatureAllowDenyListMap } from "src/states/ApplicationStateFeatureMapping";
import { Feature } from "src/states/ApplicationState";
import { GenerateGeom } from "src/managers/GenerateGeom";

export type GeoPoint = { lat: number; lon: number } | { latitude: number; longitude: number } | [number, number];
export type GeoJSONPolygon = any;

export type PLAValueType = "string" | "number" | "date" | "boolean";

export interface ConstantAttribute {
   mode: "constant";
   valueType: PLAValueType;
   value: any;
}

export interface CalculatedAttribute {
   mode: "expression";
   expression: string;
}

export type AttributeValue = ConstantAttribute | CalculatedAttribute;

export interface PLAElementRecord {
   recordType: "ELEMENT";
   id: string;
   _version?: number;
   elementType: string;
   name: string;
   geoPoint?: GeoPoint;
   geoShape?: GeoJSONPolygon;
   attributes: Record<string, AttributeValue>;
}

export interface PLARelationRecord {
   recordType: "RELATION";
   id: string;
   _version?: number;
   relationType: string;
   fromId: string;
   toId: string;
}

export type PLARecord = PLAElementRecord | PLARelationRecord;

export interface PLAAlertDefinition {
   booleanExpression: string;
   alertMessage: string;
}

export interface PLAAttributeDefinition {
   name: string;
   type: "constant" | "expression";
   valueType?: PLAValueType;
   expression?: string;
   hideAsDefault?: boolean;
   alerts?: Record<string, PLAAlertDefinition>;
}

export interface PLAElementTypeDefinition {
   name: string;
   sortOrder: number;
   color?: string;
   attributes: Record<string, PLAAttributeDefinition>;
}

export interface PLARelationTypeDefinition {
   name: string;
}

export interface PLAProject {
   id: string;
   name: string;
   description: string;
   active: boolean;
   ownerUser: string;
   ownerOrganization: string;
   allowedUsersWithRoles: { [userId: string]: string[] };
   allowedOrganizationsWithRoles: { [organizationId: string]: string[] };
   settings: any;
   esIndexName: string;
   _version?: number;
   protected: boolean;
   elementTypes: Record<string, PLAElementTypeDefinition>;
   relationTypes: Record<string, PLARelationTypeDefinition>;
}

export enum PLAUserAccessRights {
   Read = "PLA",
   Write = "PLAWriteAccess",
   Admin = "PLAAdmin"
}

export const FeaturesAccessiblePLAUserAccessRights: FeatureAllowDenyListMap = {
   [PLAUserAccessRights.Read]: {
      allow: [Feature.PLAProjectRead],
      deny: [Feature.PLAProjectWrite, Feature.PLAProjectAdmin]
   },
   [PLAUserAccessRights.Write]: {
      allow: [Feature.PLAProjectRead, Feature.PLAProjectWrite],
      deny: [Feature.PLAProjectAdmin]
   },
   [PLAUserAccessRights.Admin]: {
      allow: [Feature.PLAProjectRead, Feature.PLAProjectWrite, Feature.PLAProjectAdmin],
      deny: []
   }
};

export interface PLAProjectService {
   loadProjects(): Promise<PLAProject[]>;
   saveProject(project: PLAProject): Promise<void>;
   deleteProject(projectId: string): Promise<void>;
}

export interface PLADataService {
   loadElements(index: string): Promise<PLAElementRecord[]>;
   loadRelations(index: string): Promise<PLARelationRecord[]>;
   saveElement(element: PLAElementRecord): Promise<void>;
   saveRelation(relation: PLARelationRecord): Promise<void>;
   deleteElement(elementId: string): Promise<void>;
   deleteRelation(relationId: string): Promise<void>;
}

export interface PLAState {
   projects: PLAProject[];
   selectedProject?: PLAProject;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
}

export const PLAContext = createContext<{
   state: PLAState;
   setState: React.Dispatch<React.SetStateAction<PLAState>>;
}>({
   state: { projects: [], elements: [], relations: [] },
   setState: () => undefined
});

export const PLALocalStorageSelectedProjectKey = "PLAState-selectedProjectId";
export const PLAProjectsChangedEvent = "PLAProjectsChanged";

export function notifyPLAProjectsChanged() {
   globalThis.dispatchEvent?.(new Event(PLAProjectsChangedEvent));
}

export function getNextPLAElementTypeColor() {
   return new GenerateGeom().getNextColor() || "#2d8f85";
}

export function createDefaultPLAProject(ownerUser: string, ownerOrganization: string): PLAProject {
   const nextColor = () => getNextPLAElementTypeColor();
   return {
      id: globalThis.crypto?.randomUUID?.() || `pla-project-${Date.now()}`,
      name: "",
      description: "",
      active: true,
      ownerUser,
      ownerOrganization,
      allowedUsersWithRoles: { [ownerUser]: [PLAUserAccessRights.Read, PLAUserAccessRights.Write, PLAUserAccessRights.Admin] },
      allowedOrganizationsWithRoles: {},
      settings: {},
      esIndexName: "",
      protected: false,
      elementTypes: {
         "projektområde": {
            name: "Projektområde",
            sortOrder: 1,
            color: nextColor(),
            attributes: {
               maxEtageAreal: {
                  name: "Max samlet Etageareal",
                  type: "constant",
                  valueType: "number",
                  alerts: {
                     maxByggeretter: {
                        booleanExpression: "sum(dele.etageAreal)>[maxEtageAreal]",
                        alertMessage: "Der bygges for meget"
                     }
                  }
               },
               sumEtageAreal: {
                  name: "Samlet Etageareal",
                  type: "expression",
                  valueType: "number",
                  expression: "sum(dele.etageAreal)"
               }
            }
         },
         byggefelt: {
            name: "Byggefelt",
            sortOrder: 2,
            color: nextColor(),
            attributes: {
               etageArealSum: {
                  name: "Etageareal i alt",
                  type: "expression",
                  valueType: "number",
                  expression: "sum(dele.etageAreal)"
               }
            }
         },
         bygning: {
            name: "Bygning",
            sortOrder: 3,
            color: nextColor(),
            // Bebygget areal kommer fra BBR.
            // Etagekvadratmeter kommer også fra BBR.
            // Ubebygget areal beregnes som Grundareal fratrukket bebygget areal.
            // Anvendelsen kommer fra BBR.
            attributes: {
               bebyggetAreal: {
                  name: "Bebygget areal",
                  type: "constant",
                  valueType: "number"
               },
               etageAreal: {
                  name: "Etageareal",
                  type: "constant",
                  valueType: "number"
               },
               ubebyggetAreal: {
                  name: "Ubebygget areal",
                  type: "expression",
                  valueType: "number",
                  expression: "sum(liggerpå.regAreal) - [bebyggetAreal]"
               },
               anvendelse: {
                  name: "Anvendelse",
                  type: "constant",
                  valueType: "string"
               },
               eksisterende: {
                  name: "Eksisterende",
                  type: "constant",
                  valueType: "boolean"
               },
               skalopføres: {
                  name: "Skal opføres",
                  type: "constant",
                  valueType: "boolean"
               },
               skalnedrives: {
                  name: "Skal nedrives",
                  type: "constant",
                  valueType: "boolean"
               }
            }
         },
         bygherre: {
            name: "Bygherre", sortOrder: 4, color: nextColor(), attributes: {
               totalMatrikelareal: {
                  name: "Matrikelareal i alt",
                  type: "expression",
                  valueType: "number",
                  expression: "sum(ejer.regAreal)"
               },
               antalBygninger: {
                  name: "Antal bygninger",
                  type: "expression",
                  valueType: "number",
                  expression: "count(ejer.etageAreal)"
               },
               totalEtageAreal: {
                  name: "Etageareal i alt",
                  type: "expression",
                  valueType: "number",
                  expression: "sum(ejer.etageAreal)"
               }
            }
         },
         ejendom: {
            name: "Ejendom", sortOrder: 5, color: nextColor(), attributes: {
               totalMatrikelareal: {
                  name: "Matrikelareal i alt",
                  type: "expression",
                  valueType: "number",
                  expression: "sum(dele.regAreal)"
               }
            }
         },
         matrikel: {
            name: "Matrikel",
            sortOrder: 6,
            color: nextColor(),
            attributes: {
               matrikelNr: {
                  name: "Matrikelnummer",
                  type: "constant",
                  valueType: "string"
               },
               ejerlavsKode: {
                  name: "Ejerlavskode",
                  type: "constant",
                  valueType: "number"
               },
               regAreal: {
                  name: "Registreret areal",
                  type: "constant",
                  valueType: "number"
               },
               vejAreal: {
                  name: "Vejareal",
                  type: "constant",
                  valueType: "number"
               }
            }
         }
      },
      relationTypes: {
         dele: { name: "Dele" },
         indeholder: { name: "Indeholder" },
         ejer: { name: "Ejer" },
         liggerpå: { name: "Ligger på" }
      }
   };
}

export function getMyPLAAccessRights(project: PLAProject | undefined, userId: string, organizationId: string): string[] {
   if (!project) return [];
   const userRoles = project.allowedUsersWithRoles?.[userId] || [];
   const organizationRoles = project.allowedOrganizationsWithRoles?.[organizationId] || [];
   return Array.from(new Set([...userRoles, ...organizationRoles]));
}

export function hasPLAWriteAccess(project: PLAProject | undefined, userId: string, organizationId: string) {
   const rights = getMyPLAAccessRights(project, userId, organizationId);
   return rights.includes(PLAUserAccessRights.Write) || rights.includes(PLAUserAccessRights.Admin);
}

export function hasPLAAdminAccess(project: PLAProject | undefined, userId: string, organizationId: string) {
   return getMyPLAAccessRights(project, userId, organizationId).includes(PLAUserAccessRights.Admin);
}
